//************************************************************************ /
// CLOUDINARY UPLOAD
// Adds a cloudinary widget for uploading images.
// To function, this component requires the following script to be initialized,
// (currently being handled in _document.tsx in Acquire Flows) -
// <script
//    src="https://widget.cloudinary.com/v2.0/global/all.js"
//    type="text/javascript"
//  />
//
// For additional reference, see https://cloudinary.com/documentation/upload_widget_reference

//************************************************************************ /

import { useEffect, useState, useRef, useCallback } from "react";
import { Row, Col, FormInstance, Form, Input, Spin } from "antd";
import { CloudUploadOutlined, LoadingOutlined } from "@ant-design/icons";

export interface CloudinaryUploadProps {
  fieldName: string;
  form: FormInstance<any>;
  className?: string;
  cloudName?: string;
  apiKey?: string;
  uploadPreset?: string;
  required?: boolean;
  clientAllowedFormats?: string[];
  returnImageDetails?: boolean;
  cropping?: boolean;
  croppingAspectRatio?: number; // Width/Height - 1 for square, 0.5 for 2:1, etc.
  showSkipCropButton?: boolean;
  onUpload?: Function;
  onError?: (error: any) => void; // Callback for upload errors (e.g., 422 from content moderation)
  onSelect?: () => void; // Fires when the upload widget opens (user picks an image)
  onAbort?: () => void; // Fires if the user closes the widget without uploading
  height?: string;
}

const CloudinaryUpload = ({
  fieldName,
  form,
  className = "ph-4 mv-6",
  cloudName,
  apiKey,
  uploadPreset,
  required,
  clientAllowedFormats,
  returnImageDetails,
  cropping,
  croppingAspectRatio,
  showSkipCropButton,
  onUpload,
  onError,
  onSelect,
  onAbort,
  height = "175px",
}: CloudinaryUploadProps) => {
  // State for storing successfully uploaded urls
  const [imgLoaded, setImgLoaded] = useState(false);
  const [currentImageUrl, setCurrentImageUrl] = useState<string | null>(null);
  const [showSpin, setShowSpin] = useState(true);
  const [cloudinaryReady, setCloudinaryReady] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isUploading, setIsUploading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const dragCounterRef = useRef(0);

  // Check if Cloudinary script is loaded and ready
  useEffect(() => {
    const checkCloudinary = () => {
      const cloudinary = (window as any).cloudinary;
      if (cloudinary) {
        setCloudinaryReady(true);
        return true;
      }
      return false;
    };

    // Check immediately
    if (checkCloudinary()) return;

    // Poll for cloudinary availability (script may still be loading)
    let intervalId: number | undefined;
    intervalId = window.setInterval(() => {
      if (checkCloudinary() && intervalId) {
        window.clearInterval(intervalId);
      }
    }, 100);

    // Clean up after 10 seconds
    const timeout = window.setTimeout(() => {
      if (intervalId) window.clearInterval(intervalId);
    }, 10000);

    return () => {
      if (intervalId) window.clearInterval(intervalId);
      window.clearTimeout(timeout);
    };
  }, []);

  // Render a spinner when the component first loads, giving time to check the response and get the imgUrl.
  useEffect(() => {
    setTimeout(() => {
      setShowSpin(false);
      const existingValue = form.getFieldValue(fieldName);
      if (existingValue) {
        setImgLoaded(true);
        // Set the current image URL from existing form value
        const url = returnImageDetails
          ? existingValue?.secure_url
          : existingValue;
        setCurrentImageUrl(url);
      }
    }, 300);
  }, []);

  // Handler to open the widget
  const handleOpenWidget = useCallback(() => {
    if (!(window as any).cloudinary) {
      setUploadError("Upload service not available. Please refresh the page.");
      return;
    }

    // Clear any previous errors when starting a new upload
    setUploadError(null);

    const cloudinaryInstance = (window as any).cloudinary;

    const config = {
      cloudName:
        cloudName ||
        process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME ||
        "cotribute",
      apiKey: apiKey || process.env.NEXT_PUBLIC_CLOUDINARY_API_KEY || "",
      uploadPreset:
        uploadPreset ||
        process.env.NEXT_PUBLIC_CLOUDINARY_DEFAULT_UPLOAD_PRESET,
    };

    onSelect && onSelect();

    // Per-widget-session flag so we don't fire the legacy "No Image was uploaded"
    // event on `close`/`abort` when a `success` already happened in the same
    // session (Cloudinary fires `success` and then `close` for a single happy
    // path with `singleUploadAutoClose: false`).
    let uploadSucceeded = false;

    cloudinaryInstance.openUploadWidget(
      {
        cloudName: config.cloudName,
        apiKey: config.apiKey,
        uploadPreset: config.uploadPreset,
        multiple: false,
        singleUploadAutoClose: false, // Require user to click "Done" to close the modal
        cropping: cropping || false,
        croppingAspectRatio: croppingAspectRatio,
        showSkipCropButton: showSkipCropButton || false,
        clientAllowedFormats: clientAllowedFormats || [
          "jpeg",
          "jpg",
          "bmp",
          "tif",
          "tiff",
          "gif",
          "png",
          "eps",
          "raw",
          "cr2",
          "nef",
          "orf",
          "sr2",
          "webp",
        ],
      },
      (error: any, result: any) => {
        if (error) {
          setUploadError("Upload failed. Please try again.");
          onError && onError(error);
          return;
        }
        if (result?.event === "success") {
          if (returnImageDetails) {
            form.setFieldsValue({
              [fieldName]: result.info,
            });
          } else {
            form.setFieldsValue({
              [fieldName]: result.info.secure_url,
            });
          }
          // Update both states to trigger re-render with new image
          setCurrentImageUrl(result.info.secure_url);
          setImgLoaded(true);
          setUploadError(null);
          uploadSucceeded = true;
          onUpload && onUpload(result.info);
        }
        if (
          (result?.event === "abort" || result?.event === "close") &&
          !uploadSucceeded
        ) {
          // Mirror legacy uploadImagewidget "No Image was uploaded" event so
          // analytics can distinguish opens from completed uploads. Only fire
          // when no `success` was seen in this widget session.
          onAbort && onAbort();
        }
      }
    );
  }, [
    cloudName,
    apiKey,
    uploadPreset,
    cropping,
    croppingAspectRatio,
    showSkipCropButton,
    clientAllowedFormats,
    returnImageDetails,
    fieldName,
    form,
    onUpload,
    onError,
    onSelect,
    onAbort,
  ]);

  const handleReset = () => {
    form.setFieldsValue({
      [fieldName]: undefined,
    });
    setCurrentImageUrl(null);
    setImgLoaded(false);
  };

  // Direct upload to Cloudinary (for drag-and-drop)
  const uploadToCloudinary = useCallback(
    async (file: File) => {
      const cloudNameValue =
        cloudName ||
        process.env.NEXT_PUBLIC_CLOUDINARY_CLOUD_NAME ||
        "cotribute";
      const uploadPresetValue =
        uploadPreset ||
        process.env.NEXT_PUBLIC_CLOUDINARY_DEFAULT_UPLOAD_PRESET ||
        "ml_default";

      if (!cloudNameValue || !uploadPresetValue) {
        setUploadError("Upload configuration missing. Please try again later.");
        return;
      }

      // Clear any previous errors when starting a new upload
      setUploadError(null);
      setIsUploading(true);

      const formData = new FormData();
      formData.append("file", file);
      formData.append("upload_preset", uploadPresetValue);

      try {
        const response = await fetch(
          `https://api.cloudinary.com/v1_1/${cloudNameValue}/image/upload`,
          {
            method: "POST",
            body: formData,
          }
        );

        if (!response.ok) {
          throw new Error("Upload failed");
        }

        const result = await response.json();

        if (returnImageDetails) {
          form.setFieldsValue({
            [fieldName]: result,
          });
        } else {
          form.setFieldsValue({
            [fieldName]: result.secure_url,
          });
        }

        // Update both states to trigger re-render with new image
        setCurrentImageUrl(result.secure_url);
        setImgLoaded(true);
        setUploadError(null);
        onUpload && onUpload(result);
      } catch (error) {
        setUploadError("Upload failed. Please try again.");
        onError && onError(error);
      } finally {
        setIsUploading(false);
      }
    },
    [
      cloudName,
      uploadPreset,
      returnImageDetails,
      fieldName,
      form,
      onUpload,
      onError,
    ]
  );

  // Drag and drop handlers
  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current++;
    if (e.dataTransfer.items && e.dataTransfer.items.length > 0) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounterRef.current--;
    if (dragCounterRef.current === 0) {
      setIsDragging(false);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      setIsDragging(false);
      dragCounterRef.current = 0;

      const files = e.dataTransfer.files;
      if (files && files.length > 0) {
        const file = files[0];

        // Also check by extension since MIME types can vary
        const fileExtension = file.name.split(".").pop()?.toLowerCase();
        const allowedExtensions = clientAllowedFormats || [
          "jpeg",
          "jpg",
          "bmp",
          "tif",
          "tiff",
          "gif",
          "png",
          "eps",
          "raw",
          "cr2",
          "nef",
          "orf",
          "sr2",
        ];

        if (
          file.type.startsWith("image/") ||
          (fileExtension && allowedExtensions.includes(fileExtension))
        ) {
          uploadToCloudinary(file);
        } else {
          setUploadError("Invalid file type. Please upload an image file.");
        }
      }
    },
    [clientAllowedFormats, uploadToCloudinary]
  );

  if (showSpin) {
    return (
      <Row>
        <Col xs={24} className={className}>
          <div
            className="flex items-center justify-center br-5 s-4 w-100"
            style={{ height: height, backgroundColor: "lightgray" }}
          >
            <Spin size="large" />
          </div>
        </Col>
      </Row>
    );
  }

  return (
    <Row>
      {/* Display an upload button or an equally-sized preview of the uploaded image */}
      {imgLoaded ? (
        <Col xs={24} className={className} style={{ position: "relative" }}>
          <img
            src={currentImageUrl || ""}
            className="s-4 w-100 br-5"
            style={{ height: height, objectFit: "cover" }}
          />
          <div
            onClick={handleOpenWidget}
            className="darkenPrimaryBkd subtleLift pointer bn flex items-center justify-center br-2 ph-2 fwb absolute"
            style={{
              top: "10px",
              right: "38%",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              height: "30px",
              zIndex: 10,
            }}
          >
            Change Image
          </div>
          <div
            className="darkenPrimaryBkd subtleLift pointer bn flex items-center justify-center br-50 fwb absolute"
            style={{
              top: "10px",
              right: "30px",
              backgroundColor: "rgba(0, 0, 0, 0.5)",
              color: "white",
              width: "30px",
              height: "30px",
              zIndex: 10,
            }}
            onClick={handleReset}
          >
            X
          </div>
        </Col>
      ) : (
        <Col xs={24} className={className}>
          <div
            onClick={cloudinaryReady ? handleOpenWidget : undefined}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDragOver={handleDragOver}
            onDrop={handleDrop}
            className="flex flex-column items-center justify-center subtleLift textPrimary br-5 w-100 s-1 pointer bg-gray-1"
            style={{
              height: height,
              border: isDragging ? "2px dashed #1890ff" : "2px dashed #d9d9d9",
              backgroundColor: isDragging ? "#e6f7ff" : undefined,
              cursor: cloudinaryReady ? "pointer" : "wait",
              transition: "border-color 0.2s, background-color 0.2s",
            }}
          >
            {isUploading ? (
              <>
                <LoadingOutlined
                  style={{ fontSize: "48px", color: "#1890ff" }}
                />
                <span className="f-3 mt-2" style={{ color: "#666" }}>
                  Uploading...
                </span>
              </>
            ) : (
              <>
                <CloudUploadOutlined
                  style={{ fontSize: "48px", color: "#1890ff" }}
                />
                <span className="f-3 mt-2" style={{ color: "#666" }}>
                  {isDragging
                    ? "Drop image here"
                    : "Click or drag an image to upload"}
                </span>
              </>
            )}
          </div>
        </Col>
      )}
      {/* Display error message if upload fails */}
      {uploadError && (
        <Col xs={24} className={className} style={{ marginTop: "-16px" }}>
          <div style={{ color: "#ff4d4f", fontSize: "14px" }}>
            {uploadError}
          </div>
        </Col>
      )}
      {/* This input is hidden from the user and filled when an image is uploaded. */}
      <Form.Item
        name={fieldName}
        validateTrigger={""}
        style={{ marginTop: "-60px", pointerEvents: "none" }}
        rules={[
          {
            required: required,
            message: "Please upload an image and try again",
          },
        ]}
      >
        <Input className="dn" />
      </Form.Item>
    </Row>
  );
};

export default CloudinaryUpload;
